<template>
  <v-container fluid
               v-if="session">

    <v-card class="mb-4">
      <div class="pa-4">
        <div class="mb-2 d-flex align-center">
          <div class="d-flex align-start">
            <v-avatar color="secondary"
                      size="56"
                      class="mr-2">
              <div v-if="session.userContact"
                   class="white--text text-h5">
                {{ session.userContact.initials ? session.userContact.initials : '@' }}
              </div>
              <v-icon v-else
                      class="white--text text-h5">
                fal fa-user-secret
              </v-icon>
            </v-avatar>
            <div>
              <div v-if="session.userContact"
                   class="text-h6">
                <template v-if="session.userContact.name">
                  {{ session.userContact.name }} - {{ session.userContact.email }}
                </template>
                <template v-else>
                  {{ session.userContact.email }}
                </template>
              </div>
              <div v-else
                   class="text-h6">
                Anonymous Visitor
              </div>
              <div>
                {{ session.started_at | datetime('MMMM D, YYYY h:mm a') }}
              </div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <div class="mr-4">
            <percent-viewed-donut :percent-viewed="percentViewed"
                                  :size="56">
            </percent-viewed-donut>
          </div>
          <div>
            <div class="text-h6 text-uppercase text-right">
              Total Visit Time
            </div>
            <div class="text-right">
              {{ totalDuration }}
            </div>
          </div>
        </div>

        <v-divider class="mb-2"></v-divider>

        <div v-if="hit"
             class="mb-2">
          <h3 class="text-uppercase">
            Device, OS, & Location Info
          </h3>

          <div class="d-flex align-center mb-2">
            <div>
              <tooltip-icon v-if="hit.is_desktop"
                            icon="fal fa-desktop"
                            tip="This visit came from a desktop computer">
              </tooltip-icon>
              <tooltip-icon v-else-if="hit.is_tablet"
                            icon="fal fa-tablet">
              </tooltip-icon>
              <tooltip-icon v-else-if="hit.is_phone"
                            icon="fal fa-mobile">
              </tooltip-icon>

              <tooltip-icon v-if="hit.is_windows"
                            icon="fas fa-windows"
                            tip="Windows">
              </tooltip-icon>
              <tooltip-icon v-else-if="hit.is_macos || hit.is_iphone || hit.is_ipad"
                            icon="fas fa-apple-alt"
                            tip="Apple">
              </tooltip-icon>
              <tooltip-icon v-else-if="hit.is_android"
                            icon="fal fa-android"
                            tip="Android">
              </tooltip-icon>
            </div>

            <v-tooltip v-if="hit.is_eu"
                       right
                       class="ml-2">
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on">
                  European Union
                </div>
              </template>
              <span>
                This visit came from the European Union
              </span>
            </v-tooltip>
            <v-tooltip v-else-if="hit.location_line"
                       right
                       class="ml-2">
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on">
                  {{ hit.location_line }}
                </div>
              </template>
              <span>
                This visit came from this approximate location.
              </span>
            </v-tooltip>
          </div>

          <v-divider></v-divider>
        </div>

        <h3 class="text-uppercase">
          Total Time Spent Per Page
        </h3>
        <pages-time-viewed-chart v-if="!pagesBusy"
                                 :pages="pages">
        </pages-time-viewed-chart>
      </div>
    </v-card>
  </v-container>
</template>
<style lang="scss"
       scoped>
.Session__PageThumbnail {
  width: 80px;
  height: auto;
}
</style>
<script>
import {Form} from 'vform';
import {find, orderBy, sumBy} from 'lodash';
import analyticsService from '../../../../ml/services/analyticsService';
import TooltipIcon from './components/TooltipIcon';
import PercentViewedDonut from './components/PercentViewedDonut';
import PagesTimeViewedChart from './components/PagesTimeViewedChart';

function formatDuration(value) {
  const dt = new Date(value * 1000);
  const hours = dt.getUTCHours();
  const minutes = dt.getUTCMinutes();
  const seconds = dt.getSeconds();
  if (hours) {
    return hours.toString().padStart(2, '0') + ':' + minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
  }
  return minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
}

export default {
  components: {PagesTimeViewedChart, PercentViewedDonut, TooltipIcon},
  data() {
    return {
      events: [],
      form: new Form({
        session_id: null,
      }),
      pagesBusy: false,
      pages: [],
      session: null,
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          text: 'Content Library',
          disabled: false,
          exact: true,
          to: {name: `${this.$root.routePrefix}.home`},
        },
        {
          text: this.presentation.name,
          disabled: true,
        },
        {
          text: 'Activity',
          disabled: false,
          to: {name: `${this.$root.routePrefix}.presentation.activity`, params: {presentationID: this.session.presentation_id}},
        },
        {
          text: 'Visit',
          disabled: true,
        }
      ]
    },
    filteredEvents() {
      return this.events.filter(e => {
        return e.is_change_page && e.duration > 0;
      });
    },
    hit() {
      if (!this.session) {
        return null;
      }
      return this.session.sharedLinkHit;
    },
    pagesWithTime() {
      return this.pages.filter(p => p.total_seconds > 0);
    },
    percentViewed() {
      if (!this.pages.length) {
        return 0;
      }
      return Math.floor(this.pagesWithTime.length / this.pages.length * 100);
    },
    presentation() {
      if (!this.session) {
        return null;
      }
      return this.session.presentation;
    },
    sessionID() {
      return parseInt(this.$route.params.sessionID);
    },
    totalSeconds() {
      return sumBy(this.pages, 'total_seconds');
    },
    totalDuration() {
      return formatDuration(this.totalSeconds);
    },
  },
  created() {
    this.fetchData(this.sessionID);
  },
  methods: {
    async fetchData(sessionID) {
      this.form.startProcessing();
      await this.fetchSession(sessionID)
      await this.fetchPages();
      await Promise.all([
        this.fetchEvents(),
        this.fetchLinks(),
      ])
        .catch(() => {})
        .finally(() => {
          this.form.finishProcessing();
        })
    },
    fetchSession(sessionID) {
      return analyticsService.getSession(sessionID)
        .then(data => {
          this.session = data;
          this.form.session_id = this.session.id;
        })
    },
    fetchEvents() {
      const form = this.form.data();
      this.events = [];
      return analyticsService.getEvents(form)
        .then(data => {
          this.events = data;
          this.events.forEach(e => {
            e.page = this.pageForPath(e.path);
          })
        })
        .catch(() => {})
    },
    fetchLinks() { // MLD-2596
      const form = this.form.data();
      this.links = [];
      return analyticsService.getLinksReport(this.presentation.id, form)
        .then(data => {
          this.links = data;
        })
        .catch(() => {})
    },
    pageForPath(path) {
      return find(this.pages, p => p.path === path);
    },
    fetchPages() {
      this.pagesBusy = true;
      const pagesForm = this.form.data();
      return analyticsService.getPagesReport(this.presentation.id, pagesForm)
        .then(data => {
          this.pages = orderBy(data, ['page_number'], ['asc']);
        })
        .catch(err => {
          this.pages = [];
        })
        .finally(() => {
          this.pagesBusy = false;
        })
    },
  }
}
</script>
